import { QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import type {
  AssistantExecuteRequest,
  AssistantExecuteResponse,
  AssistantSavedTurn,
  AssistantTurnEvent,
  AssistantTurnRequest,
  AssistantTurnResponse,
} from './types'

// The assistant transport (demo `features/ai/api.ts`): streamed turns, retries with the same
// request and turn id, and abort that cancels fetch, stream and backoff alike.

class AssistantStreamError extends QuoteBuilderApiError {
  event: Extract<AssistantTurnEvent, { type: 'error' }>

  constructor(event: Extract<AssistantTurnEvent, { type: 'error' }>) {
    super(event.status ?? 502, { detail: { code: event.code } })
    this.event = event
  }
}

function retryable(error: unknown) {
  if (error instanceof QuoteBuilderApiError) {
    if (error instanceof AssistantStreamError && error.event.retryable === false) return false
    if (/(denied|invalid|expired|stale|validation|configuration|not_configured|unauthorized|forbidden|authentication)/.test(error.code)) {
      return false
    }
    return [408, 429, 502, 503, 504].includes(error.status) || (error.status === 409 && error.code === 'assistant_turn_running')
  }
  return error instanceof TypeError
}

async function retryAssistantRequest<T>(
  operation: () => Promise<T>,
  signal?: AbortSignal,
  onEvent?: (event: AssistantTurnEvent) => void,
): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    signal?.throwIfAborted()
    try {
      const result = await operation()
      signal?.throwIfAborted()
      return result
    } catch (error) {
      signal?.throwIfAborted()
      if (!retryable(error) || attempt === 4) {
        if (error instanceof AssistantStreamError) onEvent?.(error.event)
        throw error
      }
      const delay = 500 * 2 ** (attempt - 1)
      onEvent?.({ type: 'retry', attempt: attempt + 1, max_attempts: 4, delay_ms: delay })
      await new Promise<void>((resolve, reject) => {
        const abort = () => {
          clearTimeout(timer)
          reject(signal?.reason)
        }
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', abort)
          resolve()
        }, delay)
        signal?.addEventListener('abort', abort, { once: true })
      })
    }
  }
}

/** A v4 UUID; `crypto.randomUUID` is unavailable on plain-HTTP previews. */
export function newTurnId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export async function assistantTurn(
  api: QuoteBuilderApi,
  request: AssistantTurnRequest,
  onEvent?: (event: AssistantTurnEvent) => void,
  signal?: AbortSignal,
): Promise<AssistantTurnResponse> {
  signal?.throwIfAborted()
  const body = JSON.stringify({ ...request, turn_id: request.turn_id ?? newTurnId() })
  return retryAssistantRequest(() => assistantTurnAttempt(api, body, onEvent, signal), signal, onEvent)
}

async function assistantTurnAttempt(
  api: QuoteBuilderApi,
  body: string,
  onEvent?: (event: AssistantTurnEvent) => void,
  signal?: AbortSignal,
): Promise<AssistantTurnResponse> {
  const response = await api.fetch('/ai/assistant/turn', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body,
    signal,
  })
  if (!response.ok) {
    throw new QuoteBuilderApiError(response.status, await response.json().catch(() => ({})))
  }
  if (!response.headers.get('Content-Type')?.includes('text/event-stream')) {
    return response.json() as Promise<AssistantTurnResponse>
  }
  if (!response.body) throw new QuoteBuilderApiError(502, { detail: { code: 'assistant_stream_interrupted' } })
  const reader = response.body.getReader()
  const abort = () => {
    void reader.cancel().catch(() => undefined)
  }
  signal?.addEventListener('abort', abort, { once: true })
  const decoder = new TextDecoder()
  let buffer = ''
  let data: string[] = []
  const dispatch = (): AssistantTurnResponse | undefined => {
    signal?.throwIfAborted()
    if (!data.length) return undefined
    const payload = data.join('\n')
    data = []
    const event = JSON.parse(payload) as AssistantTurnEvent
    if (!event || !['progress', 'worker', 'action', 'applied', 'result', 'error', 'retry'].includes(event.type)) return undefined
    if (event.type === 'error') throw new AssistantStreamError(event)
    onEvent?.(event)
    return event.type === 'result' ? event.result : undefined
  }
  try {
    while (true) {
      signal?.throwIfAborted()
      const { value, done } = await reader.read()
      signal?.throwIfAborted()
      buffer += decoder.decode(value, { stream: !done })
      // Keep partial lines (including CRLF and UTF-8 splits) until the next chunk.
      let end: number
      while ((end = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, end).replace(/\r$/, '')
        buffer = buffer.slice(end + 1)
        if (!line) {
          const result = dispatch()
          if (result) return result
        } else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''))
      }
      if (done) throw new QuoteBuilderApiError(502, { detail: { code: 'assistant_stream_interrupted' } })
    }
  } finally {
    signal?.removeEventListener('abort', abort)
    await reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}

export const getAssistantTurns = (api: QuoteBuilderApi, quoteId?: string | null) =>
  api.request<AssistantSavedTurn[]>(
    `/ai/assistant/turns${quoteId ? `?${new URLSearchParams({ quote_id: quoteId })}` : ''}`,
  )

export const executeAssistantAction = (api: QuoteBuilderApi, request: AssistantExecuteRequest, signal?: AbortSignal) => {
  const body = JSON.stringify(request)
  return retryAssistantRequest(
    () => api.request<AssistantExecuteResponse>('/ai/assistant/execute', { method: 'POST', body, signal }),
    signal,
  )
}
